import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Shield, Plus, Trash2, Loader2, Globe, Lock, Info } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Skeleton } from '../components/ui/skeleton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function IPWhitelist() {
  const [ips, setIps] = useState([]);
  const [currentIP, setCurrentIP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedIP, setSelectedIP] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    ip_address: '',
    description: ''
  });

  const fetchIPs = async () => {
    try {
      const response = await axios.get(`${API}/ip/whitelist`);
      setIps(response.data);
    } catch (error) {
      toast.error('Failed to load IP whitelist');
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentIP = async () => {
    try {
      const response = await axios.get(`${API}/ip/check`);
      setCurrentIP(response.data);
    } catch (error) {
      console.error('Failed to check IP');
    }
  };

  useEffect(() => {
    fetchIPs();
    fetchCurrentIP();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/ip/whitelist`, formData);
      toast.success('IP added to whitelist');
      setDialogOpen(false);
      setFormData({ ip_address: '', description: '' });
      fetchIPs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add IP');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/ip/whitelist/${selectedIP.id}`);
      toast.success('IP removed from whitelist');
      setDeleteDialogOpen(false);
      setSelectedIP(null);
      fetchIPs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove IP');
    }
  };

  const addCurrentIP = () => {
    if (currentIP?.your_ip) {
      setFormData({ 
        ip_address: currentIP.your_ip, 
        description: 'Added from current session' 
      });
      setDialogOpen(true);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="ip-whitelist-page">
        <div>
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64 mt-2" />
        </div>
        <Card>
          <CardContent className="pt-6">
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ip-whitelist-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">IP Whitelist</h1>
          <p className="text-muted-foreground mt-1">
            Manage which IP addresses can access the platform
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)} data-testid="add-ip-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add IP
        </Button>
      </div>

      {/* Current IP Info */}
      {currentIP && (
        <Card className="border-primary/50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-primary/10">
                  <Globe className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Your Current IP</p>
                  <p className="font-mono font-semibold">{currentIP.your_ip}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant={currentIP.is_whitelisted ? 'default' : 'destructive'}>
                  {currentIP.is_whitelisted ? 'Whitelisted' : 'Not Whitelisted'}
                </Badge>
                {!currentIP.is_whitelisted && (
                  <Button size="sm" onClick={addCurrentIP}>
                    <Plus className="w-4 h-4 mr-1" />
                    Whitelist My IP
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Admin Bypass Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Info className="w-5 h-5" />
            Admin Bypass Link
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-2">
            Use this link to bypass IP restriction from any location:
          </p>
          <code className="block p-3 bg-muted rounded-lg font-mono text-sm break-all">
            {window.location.origin}/admin/ashutosh/1122
          </code>
          <p className="text-xs text-muted-foreground mt-2">
            This link grants temporary access. Share only with trusted administrators.
          </p>
        </CardContent>
      </Card>

      {/* IP Whitelist Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Whitelisted IPs
          </CardTitle>
          <CardDescription>
            {ips.length} IP address{ips.length !== 1 ? 'es' : ''} whitelisted
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>IP Address</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Added By</TableHead>
                <TableHead>Added At</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ips.map(ip => (
                <TableRow key={ip.id} data-testid={`ip-row-${ip.id}`}>
                  <TableCell className="font-mono font-medium">{ip.ip_address}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {ip.description || '-'}
                  </TableCell>
                  <TableCell>{ip.added_by}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {ip.added_at === 'System default' ? (
                      <Badge variant="secondary">System Default</Badge>
                    ) : (
                      new Date(ip.added_at).toLocaleDateString()
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {ip.id !== 'default' ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setSelectedIP(ip);
                          setDeleteDialogOpen(true);
                        }}
                        className="text-destructive hover:text-destructive"
                        data-testid={`delete-ip-${ip.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    ) : (
                      <Lock className="w-4 h-4 text-muted-foreground" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add IP Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add IP to Whitelist</DialogTitle>
            <DialogDescription>
              Enter the IP address you want to allow access
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="ip">IP Address *</Label>
              <Input
                id="ip"
                placeholder="192.168.1.1"
                value={formData.ip_address}
                onChange={(e) => setFormData({ ...formData, ip_address: e.target.value })}
                required
                data-testid="ip-address-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                placeholder="Office network, Home IP, etc."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                data-testid="ip-description-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-ip-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Add IP
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove IP from Whitelist</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove "{selectedIP?.ip_address}" from the whitelist? 
              Users from this IP will no longer be able to access the platform.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-ip"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
